import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * BookingChat - Interactive chat interface for booking with AI agents
 *
 * Features:
 * - Real-time chat with Max and Lucy agents
 * - Typing indicators
 * - Message history
 * - Booking details display
 * - Payment link integration
 * - Auto-scroll to latest message
 */
const BookingChat = ({ bookingPageSlug, onBookingComplete }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "agent",
      agent: "max",
      text: "Hi! I'm Max, your booking coordinator. I'm here to help you schedule an appointment. What would you like to book?",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [currentAgent, setCurrentAgent] = useState("max");
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      text: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setIsTyping(true);

    try {
      // Call the booking coordinator API
      const response = await fetch("http://localhost:8003/api/booking-coordinator/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          guest_input: inputValue.trim(),
          booking_page_slug: bookingPageSlug,
        }),
      });

      const data = await response.json();

      // Simulate typing delay
      setTimeout(() => {
        setIsTyping(false);

        if (data.status === "success") {
          // Parse the result to extract booking details
          const resultText = data.result || data.message;

          // Add agent response messages
          const agentMessages = parseAgentResponse(resultText);

          agentMessages.forEach((msg, index) => {
            setTimeout(() => {
              setMessages((prev) => [
                ...prev,
                {
                  id: Date.now() + index,
                  type: "agent",
                  agent: msg.agent || "lucy",
                  text: msg.text,
                  timestamp: new Date(),
                },
              ]);
            }, index * 1000);
          });

          // Extract booking details if available
          const details = extractBookingDetails(resultText);
          if (details) {
            setTimeout(() => {
              setBookingDetails(details);

              // Add booking confirmation message
              setMessages((prev) => [
                ...prev,
                {
                  id: Date.now() + 1000,
                  type: "booking_details",
                  details: details,
                  timestamp: new Date(),
                },
              ]);

              if (onBookingComplete) {
                onBookingComplete(details);
              }
            }, agentMessages.length * 1000 + 500);
          }
        } else {
          // Error message
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              type: "error",
              text: data.message || "Sorry, something went wrong. Please try again.",
              timestamp: new Date(),
            },
          ]);
        }

        setIsLoading(false);
      }, 1500);
    } catch (error) {
      setIsTyping(false);
      setIsLoading(false);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "error",
          text: "Unable to connect to the booking service. Please check your connection and try again.",
          timestamp: new Date(),
        },
      ]);
    }
  };

  const parseAgentResponse = (resultText) => {
    // Parse the crew result into individual messages
    // This is a simplified parser - enhance based on actual response format

    const messages = [];

    // Check if it mentions booking creation
    if (resultText.includes("booking") || resultText.includes("confirmed")) {
      messages.push({
        agent: "max",
        text: "Great! I've created your booking. Let me pass this to Lucy to send you a confirmation.",
      });

      messages.push({
        agent: "lucy",
        text: "Perfect! I've sent you a confirmation email with all the details. Check your inbox!",
      });
    } else {
      messages.push({
        agent: "max",
        text: resultText,
      });
    }

    return messages;
  };

  const extractBookingDetails = (resultText) => {
    // Extract booking details from the result text
    // This is a simplified extractor - enhance based on actual response format

    // Check if result contains booking information
    if (resultText.includes("booking") || resultText.includes("confirmed")) {
      return {
        bookingId: "BK-" + Math.random().toString(36).substr(2, 9).toUpperCase(),
        guestName: "Guest",
        date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        time: "2:00 PM",
        duration: "30 min",
        status: "confirmed",
      };
    }

    return null;
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-3xl shadow-lg">
                {currentAgent === "max" ? "👨‍💼" : "👩‍💼"}
              </div>
              <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white"></div>
            </div>
            <div>
              <h3 className="font-bold text-xl">
                {currentAgent === "max" ? "Max" : "Lucy"}
              </h3>
              <p className="text-sm text-indigo-100">
                {currentAgent === "max" ? "AI Booking Coordinator" : "AI Confirmation Specialist"}
              </p>
            </div>
          </div>
          <div className="text-xs bg-white/20 px-3 py-1.5 rounded-full font-semibold">
            AI Agent
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
        style={{ maxHeight: "calc(100vh - 250px)", minHeight: "400px" }}
      >
        <AnimatePresence>
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
        </AnimatePresence>

        {/* Typing Indicator */}
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2"
          >
            <div className="bg-white rounded-2xl px-4 py-3 shadow-sm">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
              </div>
            </div>
            <span className="text-xs text-gray-500">
              {currentAgent === "max" ? "Max" : "Lucy"} is typing...
            </span>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={isLoading}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              "Send"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * MessageBubble - Individual message component
 */
const MessageBubble = ({ message }) => {
  const isUser = message.type === "user";
  const isAgent = message.type === "agent";
  const isBookingDetails = message.type === "booking_details";
  const isError = message.type === "error";

  if (isBookingDetails) {
    return <BookingDetailsCard details={message.details} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div className={`flex gap-2 max-w-[80%] ${isUser ? "flex-row-reverse" : "flex-row"}`}>
        {/* Avatar */}
        {isAgent && (
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0 shadow-md ${
            message.agent === "max"
              ? "bg-gradient-to-br from-indigo-500 to-purple-500"
              : "bg-gradient-to-br from-purple-500 to-pink-500"
          }`}>
            {message.agent === "max" ? "👨‍💼" : "👩‍💼"}
          </div>
        )}

        {/* Message Content */}
        <div
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? "bg-indigo-600 text-white"
              : isError
              ? "bg-red-100 text-red-800 border border-red-200"
              : "bg-white text-gray-800 shadow-sm"
          }`}
        >
          {isAgent && (
            <div className={`text-sm font-bold mb-2 flex items-center gap-1 ${
              message.agent === "max" ? "text-indigo-700" : "text-purple-700"
            }`}>
              <span>{message.agent === "max" ? "👨‍💼" : "👩‍💼"}</span>
              <span>{message.agent === "max" ? "Max" : "Lucy"}</span>
              <span className="text-xs font-normal opacity-60">
                • {message.agent === "max" ? "Booking Coordinator" : "Confirmation Specialist"}
              </span>
            </div>
          )}
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
          <div className={`text-xs mt-1 ${isUser ? "text-indigo-200" : "text-gray-400"}`}>
            {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * BookingDetailsCard - Display booking confirmation details
 */
const BookingDetailsCard = ({ details }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="w-full"
    >
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 shadow-md">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white text-xl">
            ✓
          </div>
          <div>
            <h3 className="font-bold text-green-900 text-lg">Booking Confirmed!</h3>
            <p className="text-sm text-green-700">Your appointment has been scheduled</p>
          </div>
        </div>

        <div className="space-y-3 bg-white rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-sm">Booking ID:</span>
            <span className="font-semibold text-gray-900">{details.bookingId}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-sm">Guest:</span>
            <span className="font-semibold text-gray-900">{details.guestName}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-sm">Date:</span>
            <span className="font-semibold text-gray-900">{details.date}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-sm">Time:</span>
            <span className="font-semibold text-gray-900">{details.time}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-sm">Duration:</span>
            <span className="font-semibold text-gray-900">{details.duration}</span>
          </div>

          <div className="pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-sm">Status:</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                {details.status}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            📧 A confirmation email has been sent to your inbox with all the details and meeting link.
          </p>
        </div>

        {details.paymentLink && (
          <div className="mt-4">
            <a
              href={details.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3 bg-indigo-600 text-white text-center rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              💳 Complete Payment
            </a>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default BookingChat;
